import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Monitor, Plus, RefreshCw, Copy, Check, MapPin, Clock, Trash2, KeyRound,
  Sparkles, Tablet, Tv, Smartphone, Power, Pencil,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import ProvisionKioskModal from '../../components/kiosks/ProvisionKioskModal';
import EditKioskModal from '../../components/kiosks/EditKioskModal';
import { RubyLoader } from '../../components/RubyLoader';

interface Kiosk {
  kioskId: string;
  tenantId: string;
  locationId: string;
  kioskType: string;
  name: string;
  description?: string;
  provisionCode?: string;
  status: 'pending' | 'active' | 'disabled' | 'released';
  resolution?: string;
  orientation?: 'landscape' | 'portrait';
  screenSizeInches?: number;
  idlePlaylistId?: string | null;
  idleAfterSeconds?: number;
  lastSeenAt?: number;
  createdAt: number;
  activatedAt?: number;
}

interface Props {
  tenantId: string;
  tenantName: string;
}

const ONLINE_WINDOW_MS = 3 * 60 * 1000; // 3 min of no heartbeat -> offline

const KiosksTab: React.FC<Props> = ({ tenantId, tenantName }) => {
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [editingKiosk, setEditingKiosk] = useState<Kiosk | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null); // kioskId of in-flight action
  const [locationNames, setLocationNames] = useState<Record<string, string>>({});

  /* ── fetch ──────────────────────────────────────────────── */

  const fetchKiosks = useCallback(async () => {
    setLoading(true);
    try {
      const resp: any = await api.kiosks.list(tenantId);
      setKiosks(resp?.kiosks || []);
    } catch (err: any) {
      toastService.error('Failed to load kiosks: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchLocationNames = useCallback(async () => {
    try {
      const data: any = await (api as any).locationBilling.listTenantRequests(tenantId);
      const next: Record<string, string> = {};
      for (const r of data?.requests || []) {
        if (r?.locationId) next[r.locationId] = r.name || r.legalEntityName || r.locationId;
      }
      setLocationNames(next);
    } catch (err) {
      console.warn('[KiosksTab] location name lookup failed (non-fatal)', err);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchKiosks();
    fetchLocationNames();
  }, [fetchKiosks, fetchLocationNames]);

  /* ── derived ────────────────────────────────────────────── */

  const summary = useMemo(() => {
    const total = kiosks.length;
    const online = kiosks.filter(
      (k) => k.lastSeenAt && Date.now() - k.lastSeenAt < ONLINE_WINDOW_MS,
    ).length;
    const pending = kiosks.filter((k) => k.status === 'pending').length;
    return { total, online, pending };
  }, [kiosks]);

  /* ── actions ────────────────────────────────────────────── */

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleDeregister = async (k: Kiosk) => {
    if (!window.confirm(`Deregister "${k.name}"? The device will return to the pair screen. The same code stays valid.`)) {
      return;
    }
    setActionBusy(k.kioskId);
    try {
      await api.kiosks.deregister(k.kioskId, tenantId);
      toastService.success('Kiosk deregistered');
      fetchKiosks();
    } catch (err: any) {
      toastService.error(err.message || 'Failed to deregister');
    } finally {
      setActionBusy(null);
    }
  };

  const handleDelete = async (k: Kiosk) => {
    if (!window.confirm(`Permanently delete "${k.name}"? This cannot be undone.`)) {
      return;
    }
    setActionBusy(k.kioskId);
    try {
      await api.kiosks.remove(k.kioskId, tenantId);
      toastService.success('Kiosk deleted');
      fetchKiosks();
    } catch (err: any) {
      toastService.error(err.message || 'Failed to delete');
    } finally {
      setActionBusy(null);
    }
  };

  /* ── render ─────────────────────────────────────────────── */

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-black text-white px-6 py-4 rounded-lg flex items-center justify-between">
        <div>
          <h2 className="text-base md:text-lg font-semibold flex items-center gap-2">
            <Monitor size={20} />
            Kiosks
          </h2>
          <div className="text-xs text-stone-300 mt-1">
            Customer-facing kiosks for {tenantName}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchKiosks}
            className="px-3 py-2 rounded text-xs font-medium bg-white/10 hover:bg-white/20 inline-flex items-center gap-1"
            title="Refresh"
          >
            {loading ? <RubyGemMark size={14} drawing color="currentColor" /> : <RefreshCw size={14} />}
          </button>
          <button
            onClick={() => setShowProvisionModal(true)}
            className="bg-white text-black px-3 py-2 rounded text-sm font-medium hover:bg-stone-100 inline-flex items-center gap-1"
          >
            <Plus size={14} />
            Provision Kiosk
          </button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Total" value={summary.total} icon={<Monitor size={14} className="text-stone-500" />} />
        <SummaryCard label="Online" value={summary.online} icon={<Power size={14} className="text-emerald-600" />} />
        <SummaryCard label="Awaiting pair" value={summary.pending} icon={<KeyRound size={14} className="text-amber-600" />} />
      </div>

      {/* List */}
      {loading ? (
        <RubyLoader variant="section" label="Loading…" />
      ) : kiosks.length === 0 ? (
        <div className="text-center py-16 text-stone-500 border border-dashed border-stone-300 rounded-lg">
          <Monitor size={32} className="mx-auto mb-3 text-stone-400" />
          No kiosks yet.<br />
          <button
            onClick={() => setShowProvisionModal(true)}
            className="text-emerald-700 hover:underline mt-2 text-sm font-medium"
          >
            Provision your first kiosk
          </button>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Kiosk</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Location</th>
                <th className="text-left px-4 py-2">Display</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Last seen</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {kiosks.map((k) => (
                <KioskRow
                  key={k.kioskId}
                  kiosk={k}
                  locationName={locationNames[k.locationId] || k.locationId}
                  copiedCode={copiedCode}
                  onCopyCode={handleCopyCode}
                  onEdit={() => setEditingKiosk(k)}
                  onDeregister={() => handleDeregister(k)}
                  onDelete={() => handleDelete(k)}
                  busy={actionBusy === k.kioskId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Provision modal */}
      <ProvisionKioskModal
        isOpen={showProvisionModal}
        onClose={() => setShowProvisionModal(false)}
        onProvisioned={fetchKiosks}
        tenantId={tenantId}
      />

      {/* Edit modal */}
      <EditKioskModal
        isOpen={!!editingKiosk}
        kiosk={editingKiosk}
        tenantId={tenantId}
        locationName={editingKiosk ? locationNames[editingKiosk.locationId] : undefined}
        onClose={() => setEditingKiosk(null)}
        onSaved={fetchKiosks}
      />
    </div>
  );
};

/* ── tiny presentational helpers ──────────────────────────── */

const SummaryCard: React.FC<{ label: string; value: number; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="bg-white border border-stone-200 rounded-lg px-4 py-3 flex items-center justify-between">
    <div>
      <div className="text-xs text-stone-500 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold text-stone-900">{value}</div>
    </div>
    <div>{icon}</div>
  </div>
);

const TYPE_PILL: Record<string, {
  label: string;
  icon: React.FC<any>;
  cls: string;       // pill styling
  bgClass: string;   // brand-colored leading icon background
  iconColor: string; // brand-colored leading icon foreground
}> = {
  'oil-blending': {
    label: 'Oil Blending',
    icon: Sparkles,
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bgClass: 'bg-emerald-100',
    iconColor: 'text-emerald-700',
  },
};

const DEFAULT_TYPE = {
  label: 'Kiosk',
  icon: Monitor,
  cls: 'bg-stone-100 text-stone-600 border-stone-200',
  bgClass: 'bg-stone-100',
  iconColor: 'text-stone-600',
};

const STATUS_PILL: Record<string, string> = {
  active:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending:  'bg-amber-50  text-amber-700  border-amber-200',
  disabled: 'bg-stone-100 text-stone-600  border-stone-200',
  released: 'bg-rose-50   text-rose-700   border-rose-200',
};

const KioskRow: React.FC<{
  kiosk: Kiosk;
  locationName: string;
  copiedCode: string | null;
  onCopyCode: (code: string) => void;
  onEdit: () => void;
  onDeregister: () => void;
  onDelete: () => void;
  busy: boolean;
}> = ({ kiosk, locationName, copiedCode, onCopyCode, onEdit, onDeregister, onDelete, busy }) => {
  const k = kiosk;
  const TypePill = TYPE_PILL[k.kioskType] || { ...DEFAULT_TYPE, label: k.kioskType };
  const TypeIcon = TypePill.icon;
  const isOnline = !!k.lastSeenAt && Date.now() - (k.lastSeenAt as number) < ONLINE_WINDOW_MS;

  const DisplayIcon =
    k.screenSizeInches && k.screenSizeInches >= 24 ? Tv :
    k.screenSizeInches && k.screenSizeInches < 9 ? Smartphone :
    Tablet;

  return (
    <tr className="hover:bg-stone-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${TypePill.bgClass}`}>
            <TypeIcon size={16} className={TypePill.iconColor} />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-stone-900 truncate">{k.name}</div>
            {k.description && <div className="text-xs text-stone-500 truncate max-w-xs">{k.description}</div>}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${TypePill.cls}`}>
          <TypeIcon size={12} />
          {TypePill.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-stone-700 inline-flex items-center gap-1 text-sm">
          <MapPin size={12} className="text-stone-400" />
          {locationName}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="inline-flex items-center gap-1.5 text-xs text-stone-600">
          <DisplayIcon size={14} className="text-stone-400" />
          {k.screenSizeInches ? `${k.screenSizeInches}″ ` : ''}
          {k.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${STATUS_PILL[k.status] || ''}`}>
            {k.status}
          </span>
          {k.status === 'active' && (
            <span
              className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-stone-300'}`}
              title={isOnline ? 'Online — recent heartbeat' : 'Offline — no heartbeat in 3+ min'}
            />
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {k.provisionCode ? (
          <button
            onClick={() => onCopyCode(k.provisionCode!)}
            className="inline-flex items-center gap-1 text-xs font-mono text-stone-700 hover:text-stone-900"
            title="Copy code"
          >
            {k.provisionCode}
            {copiedCode === k.provisionCode ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
          </button>
        ) : (
          <span className="text-xs text-stone-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">
        {k.lastSeenAt ? (
          <span className="inline-flex items-center gap-1">
            <Clock size={12} />
            {formatRelative(k.lastSeenAt)}
          </span>
        ) : '—'}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={onEdit}
            disabled={busy}
            title="Edit display + idle settings"
            className="px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 rounded inline-flex items-center gap-1"
          >
            <Pencil size={12} />
            Edit
          </button>
          {k.status === 'active' && (
            <button
              onClick={onDeregister}
              disabled={busy}
              title="Deregister — kicks the device, keeps the code valid"
              className="px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 rounded inline-flex items-center gap-1"
            >
              <Power size={12} />
              Deregister
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={busy}
            title="Delete permanently"
            className="p-1 text-rose-600 hover:bg-rose-50 rounded"
          >
            {busy ? <RubyGemMark size={14} drawing color="currentColor" /> : <Trash2 size={14} />}
          </button>
        </div>
      </td>
    </tr>
  );
};

function formatRelative(ts: number): string {
  const delta = Date.now() - ts;
  if (delta < 60_000) return `${Math.max(1, Math.round(delta / 1000))}s ago`;
  if (delta < 3_600_000) return `${Math.round(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.round(delta / 3_600_000)}h ago`;
  return `${Math.round(delta / 86_400_000)}d ago`;
}

export default KiosksTab;
